import { useCallback, useContext, useEffect, useState } from "react";
import CustomTable from "../../../components/CustomTable";
import { ReactComponent as PlusIcons } from "../../../../../assets/icons/fi-plus-circle.svg";
import SidePopup from "../Popup/SidePopUp";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Loading from "../../../components/Loading";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { customerProfileService } from "../../../../../services/api/customerProfileService";
import { formatDate } from "../../../../../utils/DateFormat";

const columns = [
  {
    key: "id",
    title: "STT",
    minWidth: "20px",
    align: "text-left",
    render: (value) => <div className="pl-2">{value}</div>,
  },
  {
    key: "mau",
    title: "Mẫu",
    minWidth: "100px",
    align: "text-center",
    render: (value) => (
      <div className="flex items-center justify-center">
        <img
          src={value}
          alt="Hình mẫu"
          className="size-14 rounded-xl object-contain"
        />
      </div>
    ),
  },
  { key: "ten", title: "Tên", minWidth: "200px" },
  {
    key: "soDon",
    title: "Số đơn",
    minWidth: "120px",
    color: "text-[#0954E9]",
  },
  { key: "ngayNopHoSo", title: "Ngày nộp đơn", minWidth: "150px" },
  { key: "phanLoai", title: "Phân loại", minWidth: "80px" },
  { key: "soBang", title: "Số bằng", minWidth: "100px" },
  { key: "hieuLuc", title: "Ngày công bố", minWidth: "100px" },
  { key: "chuSoHuu", title: "Chủ đơn/chủ sỡ hữu", minWidth: "100px" },
  {
    key: "trangThai",
    title: "Trạng thái",
    minWidth: "120px",
    render: (value, item) => (
      <span
        className={`text-white px-3 py-1 rounded-full text-xs ${item.trangThaiColor}`}
      >
        {value}
      </span>
    ),
  },
];

const TabServiceInfo = () => {
  const { token } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const currentPage = Number(searchParams.get("page")) || 1;
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const search = searchParams.get("search") || "";

  const formatData = useCallback(
    (apiResponse) => {
      return apiResponse.map((item, index) => ({
        id: itemsPerPage * (currentPage - 1) + index + 1,
        hoSo: item.serviceId?.serviceCode || "--",
        ten: item.brand || "--",
        mau: item.logo[0]?.isImage
          ? item.logo[0]?.value
          : item.image?.url || "/images/logo.png",
        soDon: item.profileCode || "--",
        ngayNopHoSo: formatDate(item.createdDate) || "--",
        phanLoai: item.groupNames?.join("; ") || "--",
        soBang: item.numberOfCertificates || "--",
        hieuLuc: formatDate(item.dateActive) || "--",
        chuSoHuu: item.createdBy?.fullName || "--",
        trangThai: item.status || "Chưa có trạng thái",
        trangThaiColor:
          item.status === "Chờ duyệt"
            ? "bg-orange-500"
            : item.status === "Đang triển khai"
              ? "bg-blue-500"
              : item.status === "Tạm ngưng"
                ? "bg-red-500"
                : "bg-green-500",
        nav_id: item._id,
      }));
    },
    [currentPage, itemsPerPage],
  );

  const handelDuplicateProfile = async (id) => {
    try {
      setLoading(true);
      const res = await customerProfileService.duplicate_profile(id, token);
      navigate(
        `/quan-ly-dich-vu-khach-hang/ho-so/${res.profile._id}/chinh-sua`,
      );
      toast.success("Đã nhân bản hồ sơ thành công");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchData = useCallback(
    async (customerID = id, page = currentPage, limit = itemsPerPage) => {
      try {
        setLoading(true);
        const response = await customerProfileService.get_all(
          customerID,
          page,
          limit,
          token,
          search,
        );
        setData(formatData(response.data.profiles));
        setTotalPages(response.data.totalPages || 1);
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
          "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
        );
      } finally {
        setLoading(false);
      }
    },
    [id, token, currentPage, itemsPerPage, search, formatData],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading)
    return (
      <div className="flex h-full items-center justify-center mt-10">
        <Loading />
      </div>
    );

  return (
    <>
      <div className="flex items-center justify-end pb-6">
        <button
          type="button"
          onClick={() => setIsPopupOpen(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex gap-2 items-center"
        >
          <PlusIcons />
          Tạo dịch vụ mới
        </button>
      </div>
      <CustomTable
        data={data}
        columns={columns}
        currentPage={currentPage}
        setCurrentPage={(page) => {
          const params = new URLSearchParams(searchParams);
          params.set("page", page.toString());
          setSearchParams(params);
        }}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        onRowClick={(item) => {
          navigate("/quan-ly-dich-vu-khach-hang/ho-so/" + item.nav_id);
        }}
        setView
        setEdit={false}
        setDuplicate={false}
        handleDuplicate={handelDuplicateProfile}
        basePath="/quan-ly-dich-vu-khach-hang/ho-so"
      />
      <SidePopup
        id={id}
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
      />
    </>
  );
};

export default TabServiceInfo;
